import { brand } from '@enp/brand';
import type { ImportConnection, ImportConnector } from '@enp/domain/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  cn,
  EmptyState,
  FormField,
  LoadingSkeleton,
  PageHeader,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleAlert,
  Gauge,
  KeyRound,
  Lock,
  Play,
  Plug,
  RefreshCw,
  ShieldCheck,
  Unplug,
} from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { z } from 'zod';
import {
  apiErrorText,
  ImportsBreadcrumb,
  Note,
  Section,
  useImportPermissions,
  useStartRun,
} from '../../../imports/components.js';
import { rateText } from '../../../imports/labels.js';
import {
  archiveConnection,
  confirmTarget,
  connectionsQuery,
  connectorsQuery,
  createConnection,
  identifyConnection,
  importKeys,
} from '../../../imports/queries.js';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';

const searchSchema = z.object({
  connection: z.uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/imports/connect')({
  validateSearch: searchSchema,
  component: ConnectPage,
});

function Steps({ current }: { current: 1 | 2 }) {
  const steps = ['Choose the source', 'Confirm the account'];
  return (
    <ol aria-label="Steps" className="mb-6 flex flex-wrap items-center gap-3 text-sm">
      {steps.map((label, i) => {
        const n = (i + 1) as 1 | 2;
        const done = n < current;
        const active = n === current;
        return (
          <li
            key={label}
            aria-current={active ? 'step' : undefined}
            className="flex items-center gap-2"
          >
            <span
              aria-hidden
              className={cn(
                'flex size-6 items-center justify-center rounded-full border text-xs font-semibold',
                active && 'border-primary bg-primary text-primary-foreground',
                done && 'border-transparent bg-state-success-subtle text-state-success',
                !active && !done && 'border-border-strong text-muted',
              )}
            >
              {done ? <Check className="size-3.5" /> : n}
            </span>
            <span className={cn(active ? 'font-medium text-fg' : 'text-muted')}>
              {label}
              {done && <span className="sr-only"> (done)</span>}
            </span>
            {n < steps.length && <span aria-hidden className="mx-1 h-px w-8 bg-border-strong" />}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * A credential ref in words for the owner (the value itself is never shown or entered here;
 * the secret's name is a technical detail).
 */
function credentialLabel(ref: string, source: string, refs: string[]): string {
  if (ref.startsWith('builtin:')) return 'Practice data, for trying the import out';
  const shared = refs.filter((r) => !r.startsWith('builtin:'));
  const n = shared.indexOf(ref);
  return shared.length > 1 ? `${source} access ${n + 1}` : `${source} access for your business`;
}

/** Developer facts (addresses, secret names, request limits), out of the owner's way (UI1A-7). */
function TechnicalDetails({ children }: { children: ReactNode }) {
  return (
    <details className="group rounded-md border border-border px-4 py-2 text-sm">
      <summary className="cursor-pointer font-medium text-muted">Technical details</summary>
      <div className="flex flex-col gap-3 pb-1 pt-3 text-muted">{children}</div>
    </details>
  );
}

function TechFact({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Lock;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="font-medium text-fg">{label}</p>
      <p className="flex items-start gap-2">
        <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
        <span className="min-w-0">{children}</span>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- step 1

function ChooseSource({
  connectors,
  connections,
}: {
  connectors: ImportConnector[];
  connections: ImportConnection[];
}) {
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const [kind, setKind] = useState(connectors[0]?.kind ?? '');
  const connector = connectors.find((c) => c.kind === kind);
  const [ref, setRef] = useState<string | undefined>(connector?.credential_refs[0]);
  const [error, setError] = useState<string>();
  const [refused, setRefused] = useState<{
    code: string | undefined;
    title: string;
    message: string;
  }>();
  // One live connection per source (D-033): the existing one is opened instead.
  const existing = connections.find((c) => c.connector_kind === kind);
  const create = useMutation({
    mutationFn: createConnection,
    onSuccess: async (connection) => {
      await queryClient.invalidateQueries({ queryKey: importKeys.connections() });
      await navigate({ search: { connection: connection.id } });
    },
    onError: async (e) => {
      const text = apiErrorText(
        e,
        connector?.display_name ?? 'the source',
        'The connection wasn’t made',
      );
      setRefused(text);
      // Another admin may have connected meanwhile, or the grants changed.
      await queryClient.invalidateQueries({ queryKey: importKeys.connections() });
      if (text.code === 'credential_not_granted' || text.code === 'credential_missing') {
        await queryClient.invalidateQueries({ queryKey: importKeys.connectors() });
      }
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!connector || !ref) {
      setError(`Choose which ${connector?.display_name ?? 'source'} access to use.`);
      return;
    }
    setError(undefined);
    setRefused(undefined);
    create.mutate({ connector_kind: connector.kind, credential_ref: ref });
  };

  const address = ref?.startsWith('builtin:')
    ? (connector?.dev_source?.base_url ?? '')
    : (connector?.default_base_url ?? '');

  return (
    <form onSubmit={submit} className="flex max-w-2xl flex-col gap-6" noValidate>
      <Section id="source" title="Which system are you switching from?">
        <RadioGroup
          value={kind}
          onValueChange={(v) => {
            setKind(v);
            setRef(connectors.find((c) => c.kind === v)?.credential_refs[0]);
            setRefused(undefined);
          }}
          aria-label="Source system"
          className="flex flex-col gap-2"
        >
          {connectors.map((c) => (
            <label
              key={c.kind}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3',
                kind === c.kind ? 'border-primary bg-primary-subtle' : 'border-border',
              )}
            >
              <RadioGroupItem value={c.kind} />
              <span className="font-medium text-fg">{c.display_name}</span>
              {connections.some((x) => x.connector_kind === c.kind) && (
                <span className="text-sm text-muted">Already connected</span>
              )}
            </label>
          ))}
        </RadioGroup>
      </Section>

      {connector && existing && (
        <Note icon={Plug} tone="info">
          <p className="font-medium">You already have a {connector.display_name} connection.</p>
          <p className="text-muted">
            An account has one {connector.display_name} connection at a time. Open it to import, or
            disconnect it there to connect with a different credential.
          </p>
          <Button className="mt-3" variant="secondary" size="sm" asChild>
            <Link to="/imports/connect" search={{ connection: existing.id }}>
              Open the connection
              <ArrowRight aria-hidden />
            </Link>
          </Button>
        </Note>
      )}

      {connector && !existing && (
        <Section
          id="credential"
          title={`Access to ${connector.display_name}`}
          description={`${brand.productName} reads your ${connector.display_name} catalogue with read-only access that was set up for your business. You never type a password or key here.`}
        >
          {connector.credential_refs.length === 0 ? (
            <div className="flex flex-col gap-4">
              <Note icon={KeyRound} tone="warning">
                <p className="font-medium">No access is set up for your business yet.</p>
                <p className="text-muted">
                  Ask whoever runs {brand.productName} for you to set up read-only access to your{' '}
                  {connector.display_name} account, then come back. You won’t need to type anything
                  secret here.
                </p>
              </Note>
              <TechnicalDetails>
                <TechFact icon={KeyRound} label="What to set up">
                  A read-only {connector.display_name} token as a server secret whose name matches{' '}
                  <code className="break-all font-mono">{connector.credential_env_pattern}</code>,
                  granted to this account. Other accounts’ secrets are never offered here.
                </TechFact>
              </TechnicalDetails>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <FormField label="Access to use" error={error} required>
                {(control) => (
                  <Select value={ref} onValueChange={setRef}>
                    <SelectTrigger {...control} className="w-full max-w-md">
                      <SelectValue placeholder="Choose the access to use" />
                    </SelectTrigger>
                    <SelectContent>
                      {connector.credential_refs.map((r) => (
                        <SelectItem key={r} value={r}>
                          {credentialLabel(r, connector.display_name, connector.credential_refs)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormField>
              <p className="flex items-start gap-2 text-sm text-muted">
                <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0" />
                {ref?.startsWith('builtin:')
                  ? 'Practice data on this computer: nothing real is read.'
                  : `Read only, and only ever sent to ${connector.display_name} itself.`}
              </p>
              <TechnicalDetails>
                {ref && !ref.startsWith('builtin:') && (
                  <TechFact icon={KeyRound} label="Server secret">
                    <code className="break-all font-mono">{ref.slice(4)}</code>
                  </TechFact>
                )}
                <TechFact icon={Lock} label="Address">
                  <span className="break-all font-mono">{address}</span>
                </TechFact>
                <TechFact icon={Gauge} label="Request limit">
                  At most {rateText(connector.rate_ceiling)}, shared by every import of this
                  connection, so {connector.display_name} keeps working for your other apps.
                </TechFact>
              </TechnicalDetails>
            </div>
          )}
        </Section>
      )}

      {refused && (
        <Note icon={CircleAlert} tone="danger" role="alert">
          <p className="font-medium">{refused.title}</p>
          <p className="text-muted">{refused.message}</p>
        </Note>
      )}

      <div className="flex flex-wrap gap-3">
        <Button
          type="submit"
          icon={<Plug aria-hidden />}
          loading={create.isPending}
          disabled={!connector || !!existing || connector.credential_refs.length === 0}
        >
          {create.isPending ? 'Connecting…' : 'Connect'}
        </Button>
        <Button variant="secondary" asChild>
          <Link to="/imports">Cancel</Link>
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------- step 2

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-body text-fg">{children}</dd>
    </div>
  );
}

function Disconnect({ connection }: { connection: ImportConnection }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const source = connection.display_name;
  const archive = useMutation({
    mutationFn: () => archiveConnection(connection.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: importKeys.connections() });
      await queryClient.invalidateQueries({ queryKey: importKeys.runs() });
      toast.success(`${source} disconnected`, {
        description: 'Imports already loaded stay in the catalogue. You can connect again.',
      });
      await navigate({ to: '/imports' });
    },
    onError: (e) => {
      const text = apiErrorText(e, source, 'The connection wasn’t disconnected');
      toast.error(text.title, { description: text.message });
    },
  });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" icon={<Unplug aria-hidden />} loading={archive.isPending}>
          {archive.isPending ? 'Disconnecting…' : 'Disconnect'}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disconnect {source}?</AlertDialogTitle>
          <AlertDialogDescription>
            Imports stop reading from it. Imports waiting for decisions or a load are cancelled;
            what was already loaded stays in the catalogue, and your import rules and decisions are
            kept. To import again, connect it again (for example with a different credential).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction destructive onClick={() => archive.mutate()}>
            Disconnect
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ConfirmAccount({
  connection,
  connector,
}: {
  connection: ImportConnection;
  connector: ImportConnector | undefined;
}) {
  const queryClient = useQueryClient();
  const { isOwner, canEdit } = useImportPermissions();
  const { start, pending: starting } = useStartRun();
  const [error, setError] = useState<string>();
  const source = connection.display_name;
  const target = connection.reported_target;
  const confirmed = connection.target_confirmed_at !== null && connection.mode !== 'off';

  const refresh = (c: ImportConnection) =>
    queryClient.setQueryData(
      importKeys.connections(),
      (old: { connections: ImportConnection[] } | undefined) =>
        old ? { connections: old.connections.map((x) => (x.id === c.id ? c : x)) } : old,
    );

  const identify = useMutation({
    mutationFn: () => identifyConnection(connection.id),
    onSuccess: refresh,
    onError: (e) => {
      const text = apiErrorText(e, source, 'The source didn’t answer');
      setError(`${text.title}. ${text.message}`);
    },
  });
  const confirm = useMutation({
    mutationFn: () => confirmTarget(connection.id, target?.identity ?? ''),
    onSuccess: (c) => {
      refresh(c);
      setError(undefined);
      toast.success('Account confirmed', {
        description: `Imports now read ${target?.name ?? 'this account'} only.`,
      });
    },
    onError: (e) => {
      const text = apiErrorText(e, source, 'The account wasn’t confirmed');
      setError(`${text.title}. ${text.message}`);
    },
  });

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <Section
        id="account"
        title={confirmed ? 'Account confirmed' : `Is this your ${source} account?`}
        description={
          confirmed
            ? 'Every import checks first that the source still answers as this account.'
            : `${source} says the credential opens this account. Check the name before you confirm it.`
        }
      >
        {target ? (
          <dl className="grid gap-4 sm:grid-cols-2">
            <Fact label="Business name">
              <span className="text-lg font-semibold">{target.name ?? 'No name given'}</span>
            </Fact>
            <Fact label="Account id">
              <span className="break-all font-mono text-sm">{target.identity}</span>
            </Fact>
            <Fact label={confirmed ? 'Confirmed' : 'Asked'}>
              <AccountDate value={connection.target_confirmed_at ?? target.at} withTime />
            </Fact>
            <Fact label="Access">Read only</Fact>
          </dl>
        ) : (
          <Note icon={CircleAlert} tone="warning">
            <p className="font-medium">{source} hasn’t said which account this is yet.</p>
            {connection.last_error && <p className="text-muted">{connection.last_error}</p>}
          </Note>
        )}
        {target && (
          <div className="mt-4">
            <TechnicalDetails>
              <TechFact icon={Gauge} label="Request limit">
                {rateText(connection.rate_budget)}.{' '}
                {connector &&
                  (connection.rate_budget.max_requests * connector.rate_ceiling.window_seconds <
                  connector.rate_ceiling.max_requests * connection.rate_budget.window_seconds
                    ? `Below the most allowed: ${rateText(connector.rate_ceiling)}.`
                    : `The most allowed for ${source}.`)}
              </TechFact>
              <TechFact icon={Lock} label="Address">
                <span className="break-all font-mono">{connection.base_url}</span>
              </TechFact>
            </TechnicalDetails>
          </div>
        )}
        {connection.mode === 'off' && connection.target_confirmed_at && (
          <Note icon={CircleAlert} tone="danger" className="mt-4">
            <p>
              The connection is switched off: the last import found a different account than the one
              confirmed. Check the account below and confirm it again.
            </p>
          </Note>
        )}
      </Section>

      <Note icon={ShieldCheck} tone="success">
        <p className="font-medium">
          {brand.productName} only reads from {source}.
        </p>
        <p className="text-muted">
          It reads items, categories, suppliers, stores and stock levels. It never creates, changes
          or deletes anything in {source}. Before every import it asks {source} which account it is;
          if the answer changes, nothing is imported and the connection switches off.
        </p>
      </Note>

      {error && (
        <p role="alert" className="text-sm text-state-danger">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {confirmed ? (
          canEdit && (
            <Button
              icon={<Play aria-hidden />}
              loading={starting}
              onClick={() => start(connection.id)}
            >
              {starting ? 'Starting…' : 'Start an import'}
            </Button>
          )
        ) : target && isOwner ? (
          <Button
            icon={<Check aria-hidden />}
            loading={confirm.isPending}
            onClick={() => confirm.mutate()}
          >
            {confirm.isPending ? 'Confirming…' : `Yes, this is our account`}
          </Button>
        ) : null}
        {canEdit && (
          <Button
            variant="secondary"
            icon={<RefreshCw aria-hidden />}
            loading={identify.isPending}
            onClick={() => identify.mutate()}
          >
            {identify.isPending ? 'Asking…' : `Ask ${source} again`}
          </Button>
        )}
        <Button variant="ghost" asChild>
          <Link to="/imports">
            <ArrowLeft aria-hidden />
            Back to imports
          </Link>
        </Button>
        {canEdit && <Disconnect connection={connection} />}
      </div>
      {!confirmed && !isOwner && (
        <Note icon={Lock}>
          <p>
            Only the owner can confirm which account is imported. Ask them to open this page and
            confirm it.
          </p>
        </Note>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- page

function ConnectPage() {
  useDocumentTitle('Connect a source');
  const { connection: connectionId } = Route.useSearch();
  const connectors = useQuery(connectorsQuery());
  const connections = useQuery(connectionsQuery());
  const connection = connections.data?.connections.find((c) => c.id === connectionId);
  const step = connectionId ? 2 : 1;

  let body: ReactNode;
  const failed = connectors.error ?? connections.error;
  if (failed) {
    body = (
      <ErrorPanel
        error={failed}
        titleAs="h2"
        what="This page"
        onRetry={() => {
          void connectors.refetch();
          void connections.refetch();
        }}
      />
    );
  } else if (!connectors.data || !connections.data) {
    body = <LoadingSkeleton variant="form" label="Loading sources" />;
  } else if (step === 2) {
    body = connection ? (
      <ConfirmAccount
        connection={connection}
        connector={connectors.data.connectors.find((c) => c.kind === connection.connector_kind)}
      />
    ) : (
      <EmptyState
        icon={Plug}
        title="Connection not found"
        description="There’s no connection at this address. It may belong to another account."
        action={
          <Button asChild>
            <Link to="/imports">Back to imports</Link>
          </Button>
        }
      />
    );
  } else if (connectors.data.connectors.length === 0) {
    body = (
      <EmptyState
        icon={Plug}
        title="No sources available"
        description="This server has no import connectors installed."
      />
    );
  } else {
    body = (
      <ChooseSource
        connectors={connectors.data.connectors}
        connections={connections.data.connections}
      />
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={<ImportsBreadcrumb page="Connect a source" />}
        title={step === 1 ? 'Connect a source' : 'Confirm the source account'}
        description="Connect the till system you’re switching from. The owner confirms which account it is before anything is read."
      />
      <Steps current={step} />
      {body}
    </>
  );
}
